import React, { useState, useEffect } from 'react';
import { Trash2, Plus } from 'lucide-react';
import { useAPI } from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const EditableCell = ({ value, onSave, type = 'text' }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);

  const handleSave = () => {
    onSave(editValue);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <input
        type={type}
        className="px-2 py-1 border rounded-md w-full"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyPress={(e) => e.key === 'Enter' && handleSave()}
        autoFocus
      />
    );
  }

  return (
    <div 
      className="cursor-pointer hover:bg-gray-50 px-2 py-1 rounded"
      onClick={() => setIsEditing(true)}
    >
      {value}
    </div>
  );
};

const InviteUserModal = ({ isOpen, onClose, roles, onInvite }) => {
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    role_id: roles.filter(r => !['super_admin', 'physician'].includes(r.role_name))[0]?.role_id || ''
  });

  useEffect(() => {
    if (roles.length > 0) {
      const filteredRoles = roles.filter(r => !['super_admin', 'physician'].includes(r.role_name));
      setFormData(prev => ({
        ...prev,
        role_id: filteredRoles[0]?.role_id || ''
      }));
    }
  }, [roles]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await onInvite({
      ...formData,
      role_id: parseInt(formData.role_id, 10)
    });
    onClose();
    setFormData({ email: '', name: '', role_id: roles.filter(r => !['super_admin', 'physician'].includes(r.role_name))[0]?.role_id || '' });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h2 className="text-xl font-semibold mb-4">Invite New User</h2>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Name</label>
              <input
                type="text"
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Role</label>
              <select
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                value={formData.role_id}
                onChange={(e) => setFormData({ ...formData, role_id: e.target.value })}
              >
                {roles.filter(r => !['super_admin', 'physician'].includes(r.role_name)).map(role => (
                  <option key={role.role_id} value={role.role_id}>
                    {role.role_name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Send Invitation
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const UserManagement = () => {
  const api = useAPI();
  const { sessionStatus } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

  useEffect(() => {
    if (sessionStatus === 'expired') {
      navigate('/login');
    } else if (sessionStatus === 'error') {
      toast.error('Having trouble connecting to the server. Will retry shortly...');
    }
  }, [sessionStatus, navigate]);

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  const fetchUsers = async () => {
    try {
      const { data } = await api.get('/users/firm');
      setUsers(data);
    } catch (error) {
      if (error.message === 'Session expired') {
        return; // API service will handle redirect
      }
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const { data } = await api.get('/roles');
      setRoles(data);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const handleUpdate = async (userId, field, value) => {
    try {
      const updateData = {
        user_id: userId,
        [field]: field === 'role_id' ? parseInt(value, 10) : value
      };
      
      await api.put(`/users/${userId}`, updateData);
      setUsers(users.map(user => 
        user.user_id === userId ? { ...user, [field]: value } : user
      ));
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Failed to update user. Please try again.');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      await api.delete(`/users/${userId}`);
      setUsers(users.filter(user => user.user_id !== userId));
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };

  const handleInviteUser = async (userData) => {
    try {
      await api.post('/users/invite', userData);
      fetchUsers(); // Refresh the list
    } catch (error) {
      console.error('Error inviting user:', error);
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">User Management</h2>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map(user => (
                <tr key={user.user_id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <EditableCell
                      value={user.email}
                      onSave={(value) => handleUpdate(user.user_id, 'email', value)}
                      type="email"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <EditableCell
                      value={user.name}
                      onSave={(value) => handleUpdate(user.user_id, 'name', value)}
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <select
                      className="px-2 py-1 border rounded-md min-w-[150px]"
                      value={user.role_id}
                      onChange={(e) => handleUpdate(user.user_id, 'role_id', e.target.value)}
                    >
                      {roles.filter(r => !['super_admin', 'physician'].includes(r.role_name)).map(role => (
                        <option key={role.role_id} value={role.role_id}>
                          {role.role_name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleDelete(user.user_id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="px-6 py-4 border-t border-gray-200">
          <button
            onClick={() => setIsInviteModalOpen(true)}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <Plus className="h-5 w-5 mr-2" />
            New User
          </button>
        </div>
      </div>

      <InviteUserModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        roles={roles}
        onInvite={handleInviteUser}
      />
    </div>
  );
};

export default UserManagement; 